'use client';

import React, { createContext, useContext, useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOfflineSync, type SyncState } from '@/hooks/useOfflineSync';
import { enqueueOperation, type OperationType, type ConflictInfo,  } from '@/lib/offlineQueue';

interface SyncContextValue extends SyncState {
  enqueue: (params: {
    type: OperationType;
    table: string;
    module: string;
    recordId: string;
    data: any;
    updatedAt?: string;
  }) => void;
  dismissConflict: (operationId: string) => void;
  retryFailed: () => void;
  syncNotification: string | null;
  clearSyncNotification: () => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function useSyncContext() {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncContext must be used within SyncProvider');
  return ctx;
}

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [syncNotification, setSyncNotification] = useState<string | null>(null);

  const handleSyncComplete = useCallback((count: number) => {
    setSyncNotification(`✓ ${count} cambio${count !== 1 ? 's' : ''} sincronizado${count !== 1 ? 's' : ''}`);
    setTimeout(() => setSyncNotification(null), 4000);
  }, []);

  const handleConflict = useCallback((conflict: ConflictInfo) => {
    setSyncNotification(`⚠️ Conflicto en ${conflict.module}: registro modificado desde otro dispositivo`);
    setTimeout(() => setSyncNotification(null), 8000);
  }, []);

  const syncState = useOfflineSync({
    userId: user?.id || null,
    onSyncComplete: handleSyncComplete,
    onConflict: handleConflict,
  });

  const enqueue = useCallback((params: {
    type: OperationType;
    table: string;
    module: string;
    recordId: string;
    data: any;
    updatedAt?: string;
  }) => {
    if (!user?.id) return;
    enqueueOperation({
      type: params.type,
      table: params.table,
      module: params.module,
      recordId: params.recordId,
      data: params.data,
      userId: user.id,
      updatedAt: params.updatedAt || new Date().toISOString(),
      maxAttempts: 8,
    });
  }, [user?.id]);

  const clearSyncNotification = useCallback(() => setSyncNotification(null), []);

  return (
    <SyncContext.Provider value={{
      ...syncState,
      enqueue,
      dismissConflict: syncState.dismissConflict,
      retryFailed: syncState.retryFailed,
      syncNotification,
      clearSyncNotification,
    }}>
      {children}
    </SyncContext.Provider>
  );
}
